import { useOutletContext } from "react-router-dom";
import { Grid } from "@mui/material";
import { Initiative } from "~/operations";
import { SpaceOrWorkspaceScope, SpaceScope } from "~/hooks/useScope";
import { EmptySection } from "~/components/vulnerabilities";
import { Loading, LoadingFailed } from "~/components/loading";
import { Flex } from "~/components/Flex";
import { NoMatchFilters } from "~/pages/inventory/components/EmptyState";
import { FleetIcon } from "~/icons";
import { useInitiativeAssets } from "./hooks/useInitiativeAssets";
import { InitiativeAssetsTable } from "./components/InitiativeAssetsTable";

interface InitiativeDetailOutletContext {
  initiative: Initiative;
  scope: SpaceOrWorkspaceScope;
  spaceScope: SpaceScope;
}

export function InitiativeAssetsTab() {
  const { initiative, scope } =
    useOutletContext<InitiativeDetailOutletContext>();

  const {
    loading,
    error,
    assets,
    totalCount,
    pageInfo,
    fetchMore,
    sort,
    paginate,
    filterForm,
    activeFilterCount,
    resetFilters,
  } = useInitiativeAssets({
    initiativeMrn: initiative.mrn,
  });

  if (error) {
    return <LoadingFailed what="assets" />;
  }

  if (loading) {
    return <Loading what="assets" />;
  }

  const hasAssets = (assets?.length || 0) > 0;

  return (
    <Grid id="initiative-assets" size={12}>
      <Flex flexDirection="column">
        {(hasAssets || (!hasAssets && activeFilterCount > 0)) && (
          <Grid size={12} sx={{ mb: 3 }}>
            {filterForm}
          </Grid>
        )}
        {hasAssets ? (
          <InitiativeAssetsTable
            assets={assets}
            sort={sort}
            filteredTotalCount={totalCount}
            fetchMore={fetchMore}
            pageInfo={pageInfo}
            paginationProps={paginate.pageProps(pageInfo)}
            scopeParams={scope.params}
          />
        ) : (
          <>
            {activeFilterCount > 0 ? (
              <NoMatchFilters
                title="No assets match your filter selection"
                icon={
                  <FleetIcon sx={{ fontSize: 38, color: "primary.main" }} />
                }
                content={`Out of the assets, none match the filters you selected. Reset them to reveal all assets.`}
                onClick={() => {
                  resetFilters();
                }}
              />
            ) : (
              <EmptySection
                id="initiative-assets-list-empty"
                text="There are currently no assets for this initiative."
              />
            )}
          </>
        )}
      </Flex>
    </Grid>
  );
}
